import { useState, useEffect } from 'react';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import '../../css/common-styles.css';

import errorContent from '../../utils/ErrorMessages.js';
import useApi from '../../utils/useApi';
import infoIcon from '../../images/info_icon.svg';
import loadIcon from '../../images/loader-transparent.svg';
import Error from '../Error/Error.jsx';
import Toaster from '../Toaster/Toaster.jsx';

import styles from './TemplateMapping.module.css';

const TemplateMapping = () => {
  const [mappingData, setMappingData] = useState({
    templateId: '',
    roles: [],
    propertiesList: [],
    objectMappings: [] // Changed from fieldMappingList to objectMappings
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [availableObjects, setAvailableObjects] = useState([]);
  const [documentTitle, setDocumentTitle] = useState('');
  const [toasterMessage, setToasterMessage] = useState({ show: false, message: '', type: 'error' });
  const [showErrorPage, setShowErrorPage] = useState(false);
  const [errorPageConfig, setErrorPageConfig] = useState({});
  const [saveRetryCount, setSaveRetryCount] = useState(0);
  const [searchInputs, setSearchInputs] = useState({});
  const [filteredProperties, setFilteredProperties] = useState({});
  const [showSuggestions, setShowSuggestions] = useState({});
  // Store fetched HubSpot properties by object type
  const [objectPropertiesByType, setObjectPropertiesByType] = useState({});
  const [popup, setPopup] = useState('')
  // Controls whether prefilled HubSpot properties are writable by recipients during signing
  const [enableHubSpotWriteback, setEnableHubSpotWriteback] = useState(false);

  const supportedObjectTypes = new Set([
    'textbox',
    'checkbox',
    'editabledate',
    'dropdown',
    'dateSigned'
  ]);
  // Map BoldSign fieldType -> human-readable label for display after the field name.
  // The checkbox entry is overridden to "Group Checkbox" when the row represents
  // a collapsed group of checkbox members (see getMappingRows).
  const boldSignFieldTypeLabels = {
    textbox: 'Textbox',
    checkbox: 'Checkbox',
    editabledate: 'Editable Date',
    dropdown: 'Dropdown',
    dateSigned: 'Date Signed'
  };
  const getBoldSignFieldTypeLabel = (field, row) => {
    if (!field) return '';
    if (field.fieldType === 'checkbox' && row && row.isGroup) return 'Group Checkbox';
    return boldSignFieldTypeLabels[field.fieldType] || field.fieldType || '';
  };
  // Format the HubSpot property `type` for display after the property name.
  // HubSpot returns values like "string", "number", "date", "datetime", "enumeration", "bool".
  const formatHubSpotPropertyType = (type) => {
    if (!type) return '';
    const normalized = String(type).toLowerCase();
    const labels = {
      string: 'Text',
      text: 'Text',
      number: 'Number',
      date: 'Date',
      datetime: 'DateTime',
      enumeration: 'Picklist',
      bool: 'checkbox',
      boolean: 'checkbox',
      phonenumber: 'PhoneNumber'
    };
    return labels[normalized] || (type.charAt(0).toUpperCase() + type.slice(1));
  };
  // Helper to sync searchInputs based on current field values and propertiesList
  // This ensures dropdowns display the 'label' from propertiesList, not just the 'name'
  const syncSearchInputsToProperties = (fieldMappings, propertiesList, allRoles) => {
    setSearchInputs(prevInputs => {
      const newSearchInputs = {
        ...prevInputs
      };
      fieldMappings.forEach(roleMapping => {
        roleMapping.formFields.forEach(field => {
          // Find the full field details from allRoles to get fieldType by index
          const roleObj = allRoles.find(r => r.index === roleMapping.roleIndex);
          const fullField = roleObj?.formFields?.find(f => f.id === field.id);

          if (field.value) {
            if (fullField && fullField.fieldType === 'dropdown') {
              // For BoldSign dropdowns, use the value directly
              newSearchInputs[field.id] = field.value;
            } else {
              // For HubSpot properties, find the label
              const selectedProperty = propertiesList.find(prop => prop.name === field.value);
              newSearchInputs[field.id] = selectedProperty ? (selectedProperty.label || selectedProperty.name) : '';
            }
          } else if (newSearchInputs[field.id] !== undefined) {
            // If field.value is null, clear the search input for that field
            newSearchInputs[field.id] = '';
          }
        });
      });
      return newSearchInputs;
    });
  };

  // Initialize component by fetching session data and template properties
  // Prefills mapping from backend (existing mapping).
  // Legacy templates created before field-level/role-level write-back flags only
  // carry EnableHubSpotWriteback. When that master toggle is true, missing
  // hasWritebackFields / isWritebackAllowed values are treated as true (when the
  // field has a mapped property) so the UI immediately shows legacy mapped fields
  // as write-back enabled. When the toggle is false, missing flags resolve to
  // false. Saving persists explicit booleans, so the legacy fallback only applies
  // until the first re-save.
  // Write-back can never resolve to true for a field without a selected HubSpot
  // property: an unmapped field is always normalized to false (unchecked / "No"),
  // and an unchecked role checkbox forces its fields to false as well. This keeps
  // previously saved templates with empty property mappings from rendering "Yes".
  const prefillObjectMappings = (templateRoles, dbObjectMappings, objectsToMap, writebackEnabled = false) => {
    if (!dbObjectMappings) return createObjectMappings(templateRoles || [], objectsToMap);
    // dbObjectMappings: [{objectType, fieldMappings: [{roleName, formFields: [{...}]}]}]
    return objectsToMap.map(objectType => {
      // look for prefilled mapping for this object
      const dbMap = dbObjectMappings.find(m => m.objectType === objectType);
      if (!dbMap) {
        // No mapping for this object; create empty for all roles
        return {
          objectType,
          propertiesList: [],
          fieldMappings: createFieldMappingList(templateRoles)
        };
      }
      // For this object: merge fields (by id) from templateRoles (structure) and dbMap.fieldMappings (actual values)
      const mergedFieldMappings = (templateRoles || []).map((role, roleIdx) => {
        // get db roleMapping by roleIndex to handle duplicate role names
        const dbRoleMap = (dbMap.fieldMappings || []).find(rm => rm.roleIndex === role.index || (rm.roleName === role.name && rm.roleIndex === role.index));
        // for each form field, prefer db value if it matches id
        const formFields = (role.formFields || [])
          .filter(field => supportedObjectTypes.has(field.fieldType))
          .map(field => {
            const dbFields = dbRoleMap?.formFields || [];
            const dbField = dbFields.find(f => f.id === field.id) || (field.groupName ? dbFields.find(f => f.groupName === field.groupName) : null);
            // Resolve stored field flag; write-back only applies to mapped properties.
            const storedIsWritebackAllowed = dbField?.isWritebackAllowed;
            const isLegacyField = storedIsWritebackAllowed === undefined || storedIsWritebackAllowed === null;
            const hasMappedProperty = !!(dbField && dbField.value);
            // Role-level gate: legacy (null) inherits the template toggle, but an
            // explicitly disabled role forces all its fields to write-back off.
            // This mirrors the backend rule in HubSpotWriteBackService, which skips
            // the entire role when its stored flag is explicitly false.
            const storedRoleFlag = dbRoleMap?.hasWritebackFields;
            const isRoleWritebackOff = storedRoleFlag === false;
            // A field without a selected HubSpot property can never be write-back
            // enabled, regardless of stored/legacy flags. A role with write-back
            // disabled (legacy toggle off or explicit false) likewise forces off.
            const fieldWritebackOn = isLegacyField
              ? (writebackEnabled && hasMappedProperty)
              : (!!storedIsWritebackAllowed && hasMappedProperty);
            const isWritebackAllowed = isRoleWritebackOff ? false : fieldWritebackOn;
            return {
              id: field.id,
              value: dbField ? dbField.value : null,
              type: field.fieldType,
              isWritebackAllowed,
              // Preserve groupName from the template definition (single source of truth)
              // so every member of a group keeps the same groupName. dbField may also
              ...(field.fieldType === 'checkbox' && field.groupName ? { groupName: field.groupName } : {}),
              ...(field.fieldType === 'editabledate' ? { dateFormat: field.editableDateFieldSettings?.dateFormat } : {}),
              ...(dbField && dbField.hubSpotType ? { hubSpotType: dbField.hubSpotType } : {})
            };
          });
        // Role-level write-back: recompute from the normalized field states so an
        // unchecked/disabled role can never express "write-back on" for this object.
        const hasWritebackFields = formFields.some(f => f.isWritebackAllowed);
        return formFields.length > 0 ? { roleName: role.name, roleIndex: role.index, formFields, hasWritebackFields } : null;
      }).filter(Boolean);
      return {
        objectType,
        propertiesList: [],
        fieldMappings: mergedFieldMappings
      };
    });
  };

  useEffect(() => {
    initializeComponent();
  }, []);

  const api = useApi();

  // Handle source object change to fetch HubSpot properties
  useEffect(() => {
    const selectedObjectType = availableObjects[activeTab];
    if (
      mappingData.templateId &&
      selectedObjectType
    ) {
      if (objectPropertiesByType[selectedObjectType]) {
        // Already fetched; update mappingData/objectMappings with cached
        setMappingData(prev => {
          const updatedObjectMappings = prev.objectMappings.map((objectMapping, idx) =>
            idx === activeTab
              ? { ...objectMapping, propertiesList: objectPropertiesByType[selectedObjectType] }
              : objectMapping
          );
          // Also reset search inputs/filter if needed
          const currentObj = updatedObjectMappings[activeTab];
          if (currentObj) {
            initializeFilteredPropertiesForObject(currentObj.fieldMappings, objectPropertiesByType[selectedObjectType]);
            syncSearchInputsToProperties(currentObj.fieldMappings, objectPropertiesByType[selectedObjectType], prev.roles);
          }
          return {
            ...prev,
            objectMappings: updatedObjectMappings,
            propertiesList: objectPropertiesByType[selectedObjectType]
          };
        });
      } else {
        fetchHubSpotProperties(selectedObjectType);
      }
    }
  }, [activeTab, mappingData.templateId, objectPropertiesByType, availableObjects]);

  // Toaster functions
  const showToaster = (message, type = 'error') => {
    setToasterMessage({ show: true, message, type });
  };

  const hideToaster = () => {
    setToasterMessage({ show: false, message: '', type: 'error' });
  };

  const initializeComponent = async () => {
    setIsInitializing(true);
    setError('');

    try {
      // Get templateId from session storage
      const templateId = sessionStorage.getItem('templateId');
      const hs_object_type = sessionStorage.getItem('hs_object');
      const popup = sessionStorage.getItem('popup');

      sessionStorage.removeItem('templateId');
      sessionStorage.removeItem('hs_object');
      sessionStorage.removeItem('popup');

      setPopup(popup);

      if (!templateId) {
        showToaster("This page works only within HubSpot. If you're in HubSpot, try closing and reopening. Still not working? Reconnect or contact your admin.", 'error');
        return;
      }
      let fetchedObjects = [];
      if(hs_object_type){
        fetchedObjects = [hs_object_type];
      } else {
        const supportedObjectsResponse = await api.get('/hubspot/supported-objects', {});

        if (!supportedObjectsResponse.status === 200) {
          throw new Error(`Failed to fetch supported HubSpot objects`);
        }
        fetchedObjects = supportedObjectsResponse.data || [];
        
      }
      setAvailableObjects(fetchedObjects);

      // Fetch template properties
      const templateResponse = await api.get(
        `/boldsign/get-template-mapping`,
        {
          params: {
            templateId
          }
        }
      );

      if (templateResponse.status !== 200) {
        const errorData = templateResponse?.data;
        const parsed = typeof errorData === 'string' ? (() => { try { return JSON.parse(errorData); } catch { return {}; } })() : (errorData || {});
        if (parsed.error === 'TemplateNotSupported') {
          setErrorPageConfig({
            title: 'Template Not Supported.',
            message: 'The selected template cannot be used with the HubSpot integration because it includes unsupported settings. Please choose a different template to continue. You can close this window now.'
          });
        } else {
          setErrorPageConfig({
            title: errorContent.default.title,
            message: errorContent.default.message,
          });
        }

        setShowErrorPage(true);
        return;
      }
      const mappingDataResult = await templateResponse.data;
      // Fallback: if just objectMappings, fake roles to avoid crash
      const templateRoles = mappingDataResult.roles || [];
      const dbObjectMappings = mappingDataResult.objectMappings || [];

      // Load the enableHubSpotWriteback setting (default to false if not present to maintain backward compatibility)
      const legacyWritebackEnabled = mappingDataResult.enableHubSpotWriteback !== undefined
        ? mappingDataResult.enableHubSpotWriteback
        : false;
      setEnableHubSpotWriteback(legacyWritebackEnabled);

      // Check if there are any form fields to map, if not redirect
      const hasMappableFields = templateRoles?.some(role =>
        role.formFields?.some(field => supportedObjectTypes.has(field.fieldType))
      );

      if (!hasMappableFields) {
        window.location.href = '/templateReady' + (popup ? "?popup=1" : "");
        return;
      }

      if (mappingDataResult.messageTitle) {
        setDocumentTitle(mappingDataResult.messageTitle);
      }

      // Use prefill/merge helper. The legacy toggle initializes write-back state
      // for legacy/pre-existing mappings; new mappings keep their stored values.
      const objectMappings = prefillObjectMappings(templateRoles, dbObjectMappings, fetchedObjects, legacyWritebackEnabled);

      setMappingData(prev => ({
        ...prev,
        templateId,
        roles: templateRoles,
        objectMappings
      }));

    } catch (error) {
      console.error('Error initializing component:', error);
      
      // For initialization errors, show a generic error page
      setErrorPageConfig({
        title: errorContent.default.title,
        message: errorContent.default.message
      });
      setShowErrorPage(true);
    } finally {
      setIsInitializing(false);
    }
  };

const fetchHubSpotProperties = async (selectedObjectType) => {
    setIsLoading(true);
    setError('');

    try {
      // If already present, do NOT fetch again (should not reach here if effect is correct)
      if (objectPropertiesByType[selectedObjectType]) {
        setIsLoading(false);
        return;
      }
      // Fetch HubSpot properties for the selected object type
      const response = await api.get(
        `/hubspot/get-properties`,
        {
          params:
          {
            objectType: selectedObjectType,
            templateId: mappingData.templateId
          }
        }
      );
      
      if (response.status !== 200) {       
        setErrorPageConfig({
          title: errorContent.default.title,
          message: errorContent.default.message,
        });
        setShowErrorPage(true);
        return;
      }

      const data = await response.data;

      // Store properties with 'None' option at the beginning
      const propertiesList = [{ name: 'none', label: 'None' }].concat(data.properties || []);
      // Put into cache
      setObjectPropertiesByType(prev => ({
        ...prev,
        [selectedObjectType]: propertiesList
      }));
      // Update mappingData for active tab:
      setMappingData(prev => {
        const updatedObjectMappings = prev.objectMappings.map((objectMapping, idx) =>
          idx === activeTab ? { ...objectMapping, propertiesList } : objectMapping
        );
        const currentObj = updatedObjectMappings[activeTab];
        if (currentObj) {
          initializeFilteredPropertiesForObject(currentObj.fieldMappings, propertiesList);
          syncSearchInputsToProperties(currentObj.fieldMappings, propertiesList, prev.roles);
        }
        return {
          ...prev,
          objectMappings: updatedObjectMappings,
          propertiesList
        };
      });
    } catch (error) {
      console.error('Error fetching HubSpot properties:', error);
      setErrorPageConfig({
          title: errorContent.default.title,
          message: errorContent.default.message,
        });
      setShowErrorPage(true);
    } finally {
      setIsLoading(false);
    }
  };

  const createObjectMappings = (roles, objectsToMap) => {
    const objectMappings = objectsToMap.map(objectType => ({
      objectType,
      propertiesList: [],
      fieldMappings: createFieldMappingList(roles)
    }));

    return objectMappings;
  };

  const createFieldMappingList = (roles) => {
    const fieldMappingList = [];
    roles.forEach(role => {
      if (role.formFields) {
        const formFields = [];
        role.formFields.forEach(field => {
          if (supportedObjectTypes.has(field.fieldType)) {
            const formField = {
              id: field.id,
              value: null,
              type: field.fieldType,
              // New fields start unmapped, so write-back must default to
              // unchecked ("No") regardless of the template-level toggle.
              // isWritebackAllowed only flips to true once a HubSpot property
              // is selected (see handlePropertySelect).
              isWritebackAllowed: false,
            };
            if(field.fieldType === 'editabledate') {
              formField.dateFormat = field.editableDateFieldSettings.dateFormat;
            }
            // BoldSign group-checkbox members share a non-empty `groupName`.
            // We persist it on every member so the backend can resolve the group
            // and the save payload carries the new groupName field.
            if (field.fieldType === 'checkbox' && field.groupName) {
              formField.groupName = field.groupName;
            }
            formFields.push(formField);
          }
        });
        
        if (formFields.length > 0) {
          fieldMappingList.push({
            roleName: role.name,
            roleIndex: role.index,
            formFields: formFields,
            // Derived from actual field states (all false here).
            hasWritebackFields: false
          });
        }
      }
    });

    return fieldMappingList;
  };

  const initializeFilteredPropertiesForObject = (fieldMappings, propertiesList) => {
    const initialFiltered = {};
    fieldMappings.forEach(roleMapping => {
      const seenGroups = new Set();
      roleMapping.formFields.forEach(field => {
        initialFiltered[field.id] = propertiesList;
      });
    });
    setFilteredProperties(prevFiltered => ({
      ...prevFiltered,
      ...initialFiltered
    }));
  };

  const filterProperties = (searchText, availableProperties) => {
    if (!searchText) {
      return availableProperties;
    }
    
    return availableProperties.filter(property =>
      property.label.toLowerCase().includes(searchText.toLowerCase()) ||
      property.name.toLowerCase().includes(searchText.toLowerCase())
    );
  };

  const handleSearchInputChange = (fieldId, value, availableProperties) => {
    setSearchInputs(prev => ({
      ...prev,
      [fieldId]: value
    }));

    const filtered = filterProperties(value, availableProperties);
    setFilteredProperties(prev => ({
      ...prev,
      [fieldId]: filtered
    }));

    setShowSuggestions(prev => ({
      ...prev,
      [fieldId]: true
    }));
  };

  const handlePropertySelect = (roleIndex, fieldId, row, property) => {
    const isNone = property.name === 'none';
    const selectedValue = isNone ? null : property.name;
    const displayText = isNone ? '' : property.label || property.name;
    const isWritebackSupported = isNone ? false : (property.isWritebackSupported !== false);
    //   - For group rows, every member of the same groupName (memberIds).
    //   - For individual rows, just the single field id.
    const memberIds = row?.memberIds?.length ? row.memberIds : [fieldId];
    const rowStateKey = row ? getRowStateKey(row) : fieldId;

    // Update the field mapping for the current active object 
    setMappingData(prev => {
      const updatedObjectMappings = prev.objectMappings.map((objectMapping, objectIndex) =>
        objectIndex === activeTab 
          ? {
              ...objectMapping,
              fieldMappings: objectMapping.fieldMappings.map((roleMapping, rIndex) => {
                if (rIndex !== roleIndex) return roleMapping;
                const templateRole = prev.roles.find(role => role.index === roleMapping.roleIndex);
                const groupTemplateFields = row?.isGroup ? (templateRole?.formFields || []).filter(field => field.groupName === row.groupName) : [];
                const existingFieldsById = new Map(
                  roleMapping.formFields.map(field => [field.id, field])
                );
                const fieldsToUpdate = row?.isGroup
                  ? groupTemplateFields.map(templateField =>
                      existingFieldsById.get(templateField.id) || {
                        id: templateField.id,
                        type: templateField.fieldType,
                        groupName: templateField.groupName,
                        value: null,
                        isWritebackAllowed: false
                      })
                  : roleMapping.formFields;

                const updatedFormFields = roleMapping.formFields.map(field => {
                  const shouldUpdate = row?.isGroup ? field.groupName === row.groupName : field.id === fieldId;

                  if (!shouldUpdate) {
                    return field;
                  }

                  return {
                    ...field,
                    value: selectedValue,
                    hubSpotType: isNone ? undefined : property.type,
                    // Selecting a HubSpot property must not enable write-back.
                    // The user must explicitly enable the per-field option.
                    isWritebackAllowed: false
                  };
                });
                // Recompute role-level flag.
                const hasWritebackFields = updatedFormFields.some(f => f.isWritebackAllowed);

                return {
                  ...roleMapping,
                  formFields: updatedFormFields,
                  hasWritebackFields
                };
              })
            }
          : objectMapping
        );
      return { ...prev, objectMappings: updatedObjectMappings };
    });

    // Update the search input to show selected value
    setSearchInputs(prev => {
      const next = { ...prev, [rowStateKey]: displayText };
      memberIds.forEach(id => { next[id] = displayText; });
      return next;
    });

    // Hide suggestions
    setShowSuggestions(prev => ({
      ...prev,
      [rowStateKey]: false
    }));
  };

  // Toggles write-back for a single mapped field. Recomputes the role-level
  // hasWritebackFields flag so the role checkbox can reflect partial state.
  const handleFieldWritebackToggle = (roleIndex, fieldId) => {
    setMappingData(prev => ({
      ...prev,
      objectMappings: prev.objectMappings.map((objectMapping, objectIndex) =>
        objectIndex === activeTab
          ? {
              ...objectMapping,
              fieldMappings: objectMapping.fieldMappings.map((roleMapping, rIndex) => {
                if (rIndex !== roleIndex) return roleMapping;
                const updatedFormFields = roleMapping.formFields.map(field =>
                  field.id === fieldId
                    ? { ...field, isWritebackAllowed: !field.isWritebackAllowed }
                    : field
                );
                const hasWritebackFields = updatedFormFields.some(f => f.isWritebackAllowed);
                return { ...roleMapping, formFields: updatedFormFields, hasWritebackFields };
              })
            }
          : objectMapping
      )
    }));
  };

  // Toggles write-back for all supported fields within a role. Sets every
  // supported+mapped field to the same value (checked -> all on, unchecked -> all off).
  const handleRoleWritebackToggle = (roleIndex) => {
    setMappingData(prev => ({
      ...prev,
      objectMappings: prev.objectMappings.map((objectMapping, objectIndex) =>
        objectIndex === activeTab
          ? {
              ...objectMapping,
              fieldMappings: objectMapping.fieldMappings.map((roleMapping, rIndex) => {
                if (rIndex !== roleIndex) return roleMapping;
                // Determine the target state from the current majority: if any field is
                // disabled, enable all; otherwise disable all. Mirrors HubSpot UX.
                const targetState = !roleMapping.hasWritebackFields;
                const updatedFormFields = roleMapping.formFields.map(field => {
                  const property = (objectMapping.propertiesList || []).find(p => p.name === field.value);
                  const isSupported = field.value && property && property.isWritebackSupported !== false;
                  // dateSigned fields are write-back only, but still require a mapped
                  // HubSpot property to be eligible — unmapped fields always stay off.
                  const isEligible = (field.type === 'dateSigned' && !!field.value) || isSupported;
                  return {
                    ...field,
                    isWritebackAllowed: isEligible ? targetState : false
                  };
                });
                const hasWritebackFields = updatedFormFields.some(f => f.isWritebackAllowed);
                return { ...roleMapping, formFields: updatedFormFields, hasWritebackFields };
              })
            }
          : objectMapping
      )
    }));
  };

  // Returns the write-back state for a role checkbox:
  // 'checked' | 'unchecked' | 'partial' | 'disabled'
  const getRoleCheckboxState = (roleMapping) => {
    const supportedFields = roleMapping.formFields.filter(field => {
      const property = getPropertyForField(field);
      // Write-back eligibility requires a mapped HubSpot property; unmapped fields
      // (including dateSigned without a property) never count toward the role state.
      return (field.type === 'dateSigned' && !!field.value) || !!(field.value && property && property.isWritebackSupported !== false);
    });
    if (supportedFields.length === 0) {
      // No supported mapped fields for write-back → role checkbox disabled (State 4).
      return 'disabled';
    }
    const checkedCount = supportedFields.filter(f => f.isWritebackAllowed).length;
    if (checkedCount === supportedFields.length) {
      return 'checked';
    }
    if (checkedCount === 0) {
      return 'unchecked';
    }
    return 'partial';
  };

  // Helper: get the HubSpot property object for a field's value from the mapping properties.
  const getPropertyForField = (field) => {
    // find from current object mapping propertiesList (set by fetch/cache)
    const currentObjectMapping = mappingData.objectMappings[activeTab];
    if (!currentObjectMapping) return null;
    const props = currentObjectMapping.propertiesList && currentObjectMapping.propertiesList.length > 0
      ? currentObjectMapping.propertiesList
      : mappingData.propertiesList;
    return (props || []).find(p => p.name === field.value) || null;
  };

  const handleInputFocus = (fieldId, availableProperties) => {
    const currentValue = searchInputs[fieldId] || '';
    const filtered = filterProperties(currentValue, availableProperties);
    
    // Delay hiding suggestions to allow for clicks
    setTimeout(() => {
      setFilteredProperties(prev => ({
        ...prev,
        [fieldId]: filtered
      }));
      setShowSuggestions(prev => ({
        ...prev,
        [fieldId]: true
      }));
    }, 200);
  };

  const handleInputBlur = (rowStateKey, fieldId, roleIndex, row) => {
    // Delay hiding suggestions to allow for clicks
    setTimeout(() => {
      setShowSuggestions({});

      // If input is empty on blur, clear the mapping (set to None/null)
      const currentValue = (searchInputs[rowStateKey] || '').trim();
      if (!currentValue) {
        const memberIds = row?.memberIds?.length ? row.memberIds : [fieldId];
        setMappingData(prev => ({
          ...prev,
          objectMappings: prev.objectMappings.map((objectMapping, objectIndex) =>
            objectIndex === activeTab
              ? {
                  ...objectMapping,
                  fieldMappings: objectMapping.fieldMappings.map((roleMapping, rIndex) => {
                    if (rIndex !== roleIndex) return roleMapping;
                    const updatedFormFields = roleMapping.formFields.map(field =>
                      memberIds.includes(field.id)
                        // Property removed → write-back is no longer applicable; reset to No.
                        ? { ...field, value: null, isWritebackAllowed: false }
                        : field
                    );
                    const hasWritebackFields = updatedFormFields.some(f => f.isWritebackAllowed);
                    return { ...roleMapping, formFields: updatedFormFields, hasWritebackFields };
                  })
                }
              : objectMapping
          )
        }));
      }
    }, 100);
  };

  const saveFieldMapping = async () => {
    // Build object-based mapping, explicitly emitting isWritebackAllowed (per-field)
    // and hasWritebackFields (per-role) as clean booleans so reloaded mappings
    // restore verbatim and the legacy null-fallback never re-triggers.
    // Write-back is only persisted as true when the UI can actually show "Yes":
    // the field must have a selected HubSpot property that supports write-back.
    const objectBasedMapping = mappingData.objectMappings
      .map(objectMapping => {
        const propertiesList = objectMapping.propertiesList || [];
        // Properties are fetched lazily per object tab; an unvisited tab has no list.
        // Only enforce eligibility on objects whose properties loaded this session,
        // otherwise keep the stored flag so valid saved state is not destroyed.
        const hasLoadedProperties = propertiesList.length > 0;
        const filteredFieldMappings = objectMapping.fieldMappings
          .map(roleMapping => {
            const formFields = roleMapping.formFields
              .filter(field => field.value)
              .map(field => {
                // Mirror the render-time eligibility rule so saved data always
                // matches the user-facing checkbox/label state.
                const property = propertiesList.find(p => p.name === field.value);
                const isEligible = field.type === 'dateSigned'
                  ? !!field.value
                  : !!(field.value && property && property.isWritebackSupported !== false);
                return {
                  id: field.id,
                  value: field.value,
                  type: field.type,
                  ...(field.type === 'checkbox' && field.groupName
                    ? { groupName: field.groupName }
                    : {}),
                  isWritebackAllowed: !!(field.isWritebackAllowed && (isEligible || !hasLoadedProperties)),
                  ...(field.dateFormat ? { dateFormat: field.dateFormat } : {}),
                  ...(field.hubSpotType ? { hubSpotType: field.hubSpotType } : {})
                };
              });
            const hasWritebackFields = formFields.some(f => f.isWritebackAllowed);
            return {
              roleName: roleMapping.roleName,
              roleIndex: roleMapping.roleIndex,
              formFields,
              hasWritebackFields
            };
          })
          .filter(roleMapping => roleMapping.formFields.length > 0);

        return filteredFieldMappings.length > 0 ? {
          objectType: objectMapping.objectType,
          fieldMappings: filteredFieldMappings
        } : null;
      })
      .filter(mapping => mapping !== null);

    setIsSaving(true);
    setError('');

    // The template-level toggle is derived from the actual field states: it is
    // true only when at least one mapped field is write-back enabled. This keeps
    // the legacy master gate in HubSpotWriteBackService (Step 2a, which skips
    // sync entirely when EnableHubSpotWriteback=false) aligned with what the
    // mapping UI shows — all "Yes" and no gate stuck at false would silently
    // disable the write-back the user just configured.
    const anyFieldWritebackEnabled = objectBasedMapping.some(objectMapping =>
      objectMapping.fieldMappings.some(roleMapping =>
        roleMapping.formFields.some(field => field.isWritebackAllowed)
      )
    );

    const saveTemplateRequest = {
      fieldMappingData : objectBasedMapping,
      enableHubSpotWriteback: enableHubSpotWriteback || anyFieldWritebackEnabled
    }
    const templateId = mappingData.templateId;
    try {
      const response = await api.post(`/boldsign/save-template-data`,
        saveTemplateRequest ,
        {
          params:{
            templateId
          },
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.status !== 200) {
        // Show Error page immediately on bad request/server failure
        setErrorPageConfig({
          title: errorContent.default.title,
          message: errorContent.default.message
        });
        sessionStorage.removeItem('show-configure-option');
        setShowErrorPage(true);
        return;
      }

      if (response.status === 200) {
        // Reset retry count on successful save
        setSaveRetryCount(0);
        // Redirect to template create success page
        window.location.href = '/templateConfigureSuccess' + (popup ? "?popup=1" : "");
        return;
      }

      const data = await response.data;

      if (data.success && data.redirectUrl) {
        window.location.href = data.redirectUrl;
      } else {
        // Fallback redirect to template create success page
        window.location.href = '/templateConfigureSuccess' + (popup ? "?popup=1" : "");
      }

    } catch (error) {
      console.error('Error saving field mapping:', error);
      
      // Increment retry count
      const newRetryCount = saveRetryCount + 1;
      setSaveRetryCount(newRetryCount);
      
      // Extract meaningful error message
      let errorMessage = 'We encountered an issue while saving your field mapping. Please try again.';
      
      // Show error page after 3 failed attempts
      if (newRetryCount >= 3) {
        setErrorPageConfig({
          title: errorContent.default.title,
          message: errorContent.default.message
        });
        setShowErrorPage(true);
      } else {
        // Show toaster error for first 2 attempts
        showToaster(errorMessage, 'error');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const getFormFields = (role) => {
    if (!role.formFields) return [];
    return role.formFields.filter(field => supportedObjectTypes.has(field.fieldType));
  };

  // Build the list of mapping rows for a role. Group-checkbox members share a
  // groupName and are collapsed into a single row per group (one mapping for
  // the whole HubSpot multiple-checkbox property). Non-group fields stay as
  // individual rows.
  const getMappingRows = (role, roleMapping) => {
    if (!role.formFields) return [];
    const formFields = role.formFields.filter(field => supportedObjectTypes.has(field.fieldType));
    const rows = [];
    const seenGroups = new Set();

    formFields.forEach((field) => {
      const mappedField = roleMapping?.formFields?.find(f => f.id === field.id);
      const groupName = field.groupName || mappedField?.groupName;

      if (groupName) {
        if (seenGroups.has(groupName)) return; // already represented by a row
        seenGroups.add(groupName);
        // Collect every member id in the group so picking a property still
        // propagates the value to all siblings on save.
        const memberIds = formFields
          .filter(f => f.groupName === groupName)
          .map(f => f.id);
        rows.push({
          rowKey: groupName,
          isGroup: true,
          groupName,
          memberIds,
          // Use the first member's id as the storage handle for selection/blur.
          field: { id: memberIds[0], fieldType: 'checkbox' },
          mappedField
        });
      } else {
        rows.push({
          rowKey: field.id,
          isGroup: false,
          groupName: null,
          memberIds: [field.id],
          field,
          mappedField
        });
      }
    });

    return rows;
  };

  // Returns a stable storage key for a row's search/filtered/show state.
  // Group rows share a single key per groupName; individual rows use the field id.
  const getRowStateKey = (row) => {
    if (row.isGroup) return row.groupName;
    return row.field.id;
  };

  const renderObjectContent = () => {
    const currentObjectMapping = mappingData.objectMappings[activeTab];
    if (!currentObjectMapping) return null;

    return (
      <div className={styles.objectContent}>
        {currentObjectMapping.fieldMappings.map((roleMapping, roleIndex) => {
          const roleObj = mappingData.roles.find(role => role.index === roleMapping.roleIndex);
          if (!roleObj) return null;

          const mappingRows = getMappingRows(roleObj, roleMapping);
          const fieldsCount = mappingRows.length;
          const roleCheckboxState = getRoleCheckboxState(roleMapping);
          const roleCheckboxDisabled = roleCheckboxState === 'disabled';

          return (
            <div key={`role-${roleMapping.roleIndex}-${activeTab}`} className={styles.roleSection}>
              <div className={styles.roleBadge}>
                <span className={styles.roleLabel}>Role {roleMapping.roleIndex} ({roleMapping.roleName})</span>
                <span className={styles.fieldCount}>- {fieldsCount} fields</span>
              </div>

              {mappingRows.length > 0 && (
                <div className={styles.mappingSection}>
                  <div className={styles.sectionHeader}>
                    <div className={styles.sectionTitle}>BoldSign Field ID</div>
                    <div className={styles.sectionTitle}>HubSpot Property</div>
                    <div className={`${styles.sectionTitle} ${styles.writebackHeader}`}>
                      <label className={styles.roleCheckboxLabel}>
                        <span className={styles.checkboxBoxContainer} aria-hidden="true">
                          <input
                            type="checkbox"
                            className={styles.visuallyHidden}
                            checked={roleCheckboxState === 'checked'}
                            onChange={() => handleRoleWritebackToggle(roleIndex)}
                            disabled={roleCheckboxDisabled}
                            aria-label={`Write back to HubSpot for role ${roleMapping.roleName}`}
                          />
                          <span
                            className={`${styles.roleCheckbox} ${
                              roleCheckboxState === 'checked' ? styles.checkboxChecked
                                : roleCheckboxState === 'partial' ? styles.checkboxPartial
                                : roleCheckboxState === 'disabled' ? styles.checkboxDisabled
                                : styles.checkboxUnchecked
                            }`}
                          >
                            {(roleCheckboxState === 'checked' || roleCheckboxState === 'partial') && (
                              <span className={styles.checkboxCheckmark}>
                                {roleCheckboxState === 'partial' ? '–' : '✓'}
                              </span>
                            )}
                          </span>
                        </span>
                        <span className={styles.writebackHeaderText}>Write back to HubSpot</span>
                        <span className = {`${styles.titleInfoIcon} sfIconBase sfInfoIcon`} data-tooltip-id="title-tooltip"></span>
                        </label>
                        <Tooltip
                            id="title-tooltip"
                            place="top"
                            className='tooltip-info tip-title'
                            render={() => (
                              <div className="tooltip-content">
                                When enabled, changes made to the field after signing are updated in HubSpot. When disabled, the field is only prefilled from HubSpot when sending the document.
                              </div>
                            )}
                          />
                    </div>
                  </div>

                  {mappingRows.map((row) => {
                    const formField = row.mappedField;
                    if (!formField) return null;

                    const dropdownOptions = currentObjectMapping.propertiesList.length > 0
                        ? currentObjectMapping.propertiesList
                        : mappingData.propertiesList;

                    const selectedProperty = dropdownOptions?.find(p => p.name === formField.value) || null;
                    // Write-back is only valid when a HubSpot property is actually mapped.
                    // For non dateSigned fields the mapped property must also support write-back.
                    const isWritebackSupported = formField.type === 'dateSigned'
                      ? !!formField.value
                      : !!(formField.value && selectedProperty && selectedProperty.isWritebackSupported !== false);
                    // Effective write-back state per requirement: "Yes" only when BOTH
                    // (1) a valid HubSpot property is selected AND (2) the write-back
                    // option is enabled. Anything else renders unchecked / "No".
                    const isWritebackEnabled = !!formField.isWritebackAllowed && isWritebackSupported;
                    const isDateSignedField = row.field.fieldType === 'dateSigned';

                    // Display label: a comma-joined list of member ids for a
                    // group row (e.g. "sport, music, academic"), or the single
                    // field id for an individual row.
                    const displayLabel = row.isGroup
                      ? row.memberIds.join(', ')
                      : row.field.id;
                    // For group rows, the underlying storage field id is the
                    // first member — this is what we pass to selection/blur
                    // handlers so the existing value-propagation logic in those
                    // handlers keeps writing across every sibling on save.
                    const storageFieldId = row.isGroup ? row.memberIds[0] : row.field.id;
                    const rowStateKey = getRowStateKey(row);
                    const tooltipId = row.isGroup
                      ? `group-tt-${row.groupName}`
                      : `field-tt-${row.field.id}`;

                    // Display label for the BoldSign field type. Use the
                    // row-level group flag (set by getMappingRows) so a row
                    // that was collapsed from a group of checkbox members
                    // renders as "Group Checkbox".
                    const boldSignTypeLabel = getBoldSignFieldTypeLabel(row.field, row);
                    const hubSpotTypeLabel = selectedProperty ? formatHubSpotPropertyType(selectedProperty.type) : '';

                    return (
                      <div key={row.rowKey} className={styles.mappingRow}>
                        <div className={styles.fieldLabelContainer}>
                          <div>
                            <span
                              className={styles.fieldLabel}
                              onMouseEnter={(e) => {
                                const target = e.currentTarget;
                                if (target.scrollWidth > target.clientWidth) {
                                  target.setAttribute('data-tooltip-id', tooltipId);
                                  target.setAttribute('data-tooltip-content', displayLabel);
                                } else {
                                  target.removeAttribute('data-tooltip-id');
                                  target.removeAttribute('data-tooltip-content');
                                }
                              }}
                            >
                              {row.groupName}
                              <span
                                className={row.isGroup ? `${styles.multipleFieldLabel}`: `${styles.fieldLabel}`}
                              >
                                {row.isGroup && <>&nbsp;</>} {row.isGroup ? '[' : ''}{displayLabel}{row.isGroup ? ']' : ''}
                              </span>
                            </span>
                          </div>
                            {(boldSignTypeLabel) && (
                              <span
                                className={styles.fieldTypeBadge}
                              >
                                {boldSignTypeLabel}
                              </span>
                            )}
                          <Tooltip
                            id={tooltipId}
                            place="top"
                            className="tooltip-info"
                            positionStrategy="fixed"
                          />
                        </div>
                        <div className={styles.fieldDropdown}>
                          <div className={styles.searchContainer}>
                              <div className={styles.searchInputWithType}>
                                <input
                                  type="text"
                                  className={styles.searchInput}
                                  placeholder="Search properties..."
                                  value={searchInputs[rowStateKey] !== undefined
                                    ? searchInputs[rowStateKey]
                                    : (searchInputs[storageFieldId] || '')}
                                  onChange={(e) => handleSearchInputChange(rowStateKey, e.target.value, dropdownOptions)}
                                  onFocus={() => handleInputFocus(rowStateKey, dropdownOptions)}
                                  onBlur={() => handleInputBlur(rowStateKey, storageFieldId, roleIndex, row)}
                                />
                                {hubSpotTypeLabel && (<span className={`${styles.propertyTypeBadge} ${styles.badge}`}>
                                  {hubSpotTypeLabel}
                                </span>)}
                              </div>
                            {showSuggestions[rowStateKey] && (
                              <div className={styles.suggestionsDropdown}>
                                {(filteredProperties[rowStateKey]).map(property => {
                                  const propertyTypeLabel = formatHubSpotPropertyType(property.type);
                                  return (
                                    <div
                                      key={property.name}
                                      className={styles.suggestionItem}
                                      role="button"
                                      tabIndex={0}
                                      onMouseDown={() => handlePropertySelect(roleIndex, storageFieldId, row, property)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                          handlePropertySelect(roleIndex, storageFieldId, row, property);
                                        }
                                      }}
                                    >
                                      <span className={styles.suggestionItemLabel}>
                                        {property.label || property.name}
                                      </span>
                                      {propertyTypeLabel && (
                                        <span className={`${styles.suggestionItemType} ${styles.badge}`}>
                                          {propertyTypeLabel}
                                        </span>
                                      )}
                                    </div>
                                  );
                                })}
                                {(filteredProperties[rowStateKey] || dropdownOptions).length === 0 && (
                                  <div className={styles.noResults}>No properties found</div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className={styles.writebackCell}>
                          <div className={styles.writebackCellContent}>
                            <div className={styles.writebackCellHeader}>
                              <label className={styles.roleCheckboxLabel}>
                                <span className={styles.checkboxBoxContainer} aria-hidden="true">
                                  <input
                                    type="checkbox"
                                    className={styles.visuallyHidden}
                                    checked={isWritebackEnabled}
                                    onChange={() => handleFieldWritebackToggle(roleIndex, storageFieldId)}
                                    disabled={!isWritebackSupported}
                                    aria-label={`Write back to HubSpot for field ${row.id}`}
                                  />
                                  <span
                                    className={`${styles.roleCheckbox} ${
                                      isWritebackEnabled ? styles.checkboxChecked : styles.checkboxUnchecked
                                    } ${!isWritebackSupported ? styles.checkboxDisabled : ''}`}
                                  >
                                    {isWritebackEnabled && (
                                      <span className={styles.checkboxCheckmark}>✓</span>
                                    )}
                                  </span>
                                </span>
                                <span className={styles.writebackStateText}>
                                  {isWritebackEnabled ? 'Yes' : 'No'}
                                </span>
                              </label>
                              {isDateSignedField && (
                                <>
                                  <span className = {`${styles.titleInfoIcon} sfIconBase sfInfoIcon`} data-tooltip-id="dateSigned-tooltip"></span>
                                  <Tooltip
                                    id="dateSigned-tooltip"
                                    place="top"
                                    className='tooltip-info tip-title'
                                    render={() => (
                                      <div className="tooltip-content">
                                        This field supports write-back only. It will not be prefilled when sending from HubSpot. The signing date will be updated in the mapped HubSpot field after signing.
                                      </div>
                                    )}
                                  />
                                </>
                              )}
                            </div>
                            <div className={styles.writebackFieldHelperText}>
                              {isDateSignedField
                                ? 'This field will be updated after signing. Value is not prefilled.'
                                : isWritebackEnabled
                                ? 'Changes will be updated in HubSpot after signing.'
                                : 'The field will only be prefilled from HubSpot when sending.'}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Handle error page button click
  const handleErrorPageButtonClick = () => {
    // Send DONE message to parent to close the modal/iframe
    try {
      window.parent.postMessage(JSON.stringify({ action: "DONE" }), "*");
    } catch (error) {
      console.error("Error occurred when return to HubSpot:", error);
      // Fallback: try to go back in history
      if (window.history.length > 1) {
        window.history.back();
      }
    }
  };

  const isSaveDisabled = () => {
    return isSaving || isLoading || isInitializing;
  };

  const getButtonText = () => {
    if (isSaving) return 'Saving...';
    
    return 'Save';
  };

  // Show error page if needed
  if (showErrorPage) {
    return (
      <Error
        title={errorPageConfig.title}
        message={errorPageConfig.message}
        buttonText={errorPageConfig.buttonText}
        onButtonClick={handleErrorPageButtonClick}
      />
    );
  }

  // Show loading state during initialization
  if (isInitializing) {
    return (
      <div className={styles.templateMappingWrapper}>
        {/* Toaster Component */}
        <Toaster
          show={toasterMessage.show}
          message={toasterMessage.message}
          type={toasterMessage.type}
          onClose={hideToaster}
          autoHide={true}
          autoHideDelay={3000}
        />
        
        <div className={styles.container}>
          <div className="loading-overlay">
              <img src={loadIcon} alt="loading" className="spinner-circle"/>
            </div>
          </div>
        </div>
      );
    }

  // Function to handle clearing all mappings for the current tab
  const handleClearMapping = () => {
    setMappingData(prev => {
      const updatedObjectMappings = prev.objectMappings.map((objectMapping, index) => {
        if (index === activeTab) {
          return {
            ...objectMapping,
            fieldMappings: objectMapping.fieldMappings.map(role => {
              // Clearing all property mappings demotes every field out of write-back.
              const updatedFormFields = role.formFields.map(field => ({
                ...field,
                value: null,
                isWritebackAllowed: false
              }));
              return {
                ...role,
                formFields: updatedFormFields,
                hasWritebackFields: false
              };
            })
          };
        }
        return objectMapping;
      });

      // Clear current search inputs and filtered properties for the active tab
      const currentObjectType = availableObjects[activeTab];
      const currentObjectProps = objectPropertiesByType[currentObjectType] || [];

      const newSearchInputs = {};
      const newFilteredProperties = {};
      updatedObjectMappings[activeTab].fieldMappings.forEach(roleMapping => {
        const seenGroups = new Set();
        roleMapping.formFields.forEach(field => {
          newSearchInputs[field.id] = '';
          newFilteredProperties[field.id] = currentObjectProps;
          if (field.groupName && !seenGroups.has(field.groupName)) {
            seenGroups.add(field.groupName);
            newSearchInputs[field.groupName] = '';
            newFilteredProperties[field.groupName] = currentObjectProps;
          }
        });
      });

      setSearchInputs(newSearchInputs); // Set all search inputs to empty string
      setFilteredProperties(newFilteredProperties); // Reset filtered properties
      setShowSuggestions({}); // Close any open suggestions

      return {
        ...prev,
        objectMappings: updatedObjectMappings,
      };
    });
  };

  return (
    <div className={styles.templateMappingWrapper}>
      {/* Toaster Component */}
      <Toaster
        show={toasterMessage.show}
        message={toasterMessage.message}
        type={toasterMessage.type}
        onClose={hideToaster}
        autoHide={true}
        autoHideDelay={3000}
      />
      
      <div className={styles.container}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
              Field mapping
          </div>
          {/* Document Title */}
          <div className={styles.documentTitle}>
            <h1>{documentTitle}</h1>
          </div>
          <div className={styles.headerRight}>
            <button
              className={styles.clearBtn}
              onClick={handleClearMapping}
              disabled={isSaveDisabled()}
            >
              Clear
            </button>
            <button
              className={styles.saveBtn}
              onClick={saveFieldMapping}
              disabled={isSaveDisabled()}
            >
              {getButtonText()}
            </button>
          </div>
          {isSaving && (
            <div className="loading-overlay">
              <img src={loadIcon} alt="loading" className="spinner-circle"/>
            </div>
          )}
        </div>

        {error && <div className={styles.error}>{error}</div>}
        <div className={styles.mappingSectionContainer}>
          {/* Object Tabs */}
          <div className={styles.objectTabs}>
            {availableObjects.map((objectType, index) => (
              <button
                key={objectType}
                className={`${styles.tab} ${activeTab === index ? styles.active : ''}`}
                onClick={() => setActiveTab(index)}
              >
                {objectType.charAt(0).toUpperCase() + objectType.slice(1)}
              </button>
            ))}
          </div>
          <div className={styles.description}>
            <div>Map HubSpot properties to BoldSign template fields to automatically prefill documents with data from your HubSpot record.</div>
          </div>
            <div className={styles.writebackBanner}>
              <div className={styles.writebackBannerTitleContainer}>
              <img src={infoIcon} alt="info" id="info-icon" className={styles.infoIcon} />
              <div className={styles.writebackBannerTitle}>About write-back</div>
              </div>
              <div className={styles.writebackBannerDescription}>
                Fields with write-back enabled will be updated in HubSpot after the document is signed.
              </div>
            </div>
          {/* Content */}
          <div className={styles.content}>
            {isLoading ? (
              <div className="loading-overlay">
                <img src={loadIcon} alt="loading" className="spinner-circle"/>
              </div>
            ) : (
              mappingData.objectMappings.length > 0 && renderObjectContent()
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplateMapping;